/**
 * ============================================================================
 * LOCK-CART SYSTEM — Layer 3: Cart URL & Form Block
 * ============================================================================
 *
 * Purpose: Prevent products tagged 'lock-cart' from being added to cart via:
 *   - Direct /cart/add URL hits (e.g. /cart/add?id=12345)
 *   - AJAX cart additions (fetch, XHR)
 *   - Form submissions to /cart/add
 *   - Multi-item cart-add requests using the items[N][id]= or JSON
 *     items:[{id:...}] formats. These were the original blind spot that
 *     allowed orphan-child exploits (e.g. order DTC344942) — a request
 *     containing only the Discovery Set's 2mL children and no parent.
 *
 * Bundle exception: a multi-item request that ALSO contains an item
 * declaring itself the bundle parent (properties._bundle_product) is
 * allowed through, provided that parent's own variant is not itself
 * lock-cart-tagged. This is what makes the Discovery Set's legitimate
 * 5-children-plus-parent payload work.
 *
 * Ready-gate: cart-add requests fired before the blocklist finishes
 * loading are HELD (not allowed through) until LOCK_CART.ready is true,
 * with a READY_TIMEOUT_MS failsafe so legit traffic can never get stuck.
 * This closes the race condition where an app like a free-gift upsell
 * fires its auto-add during the brief window after page parse but
 * before /products.json has finished paginating.
 *
 * How it works:
 *   1. On page load, fetch ALL products tagged 'lock-cart' from Shopify
 *      (paginated — handles unlimited catalog size)
 *   2. Build a list of their variant IDs
 *   3. Intercept ALL cart-add requests and reject any matching variant ID
 *      (holding requests during step 1 if needed)
 *
 * To add a new locked product: just tag it 'lock-cart' in Shopify Admin.
 * No code changes required. Works with any catalog size.
 * ============================================================================
 */

(function () {
  'use strict';

  // Global storage for blocked variant IDs
  window.LOCK_CART = window.LOCK_CART || { blockedVariants: [], ready: false };

  const LOCK_TAG = 'lock-cart';
  const PAGE_SIZE = 250; // Shopify's max per page
  const MAX_PAGES = 100; // Safety limit (handles up to 25,000 products)
  const BLOCK_MESSAGE = 'This product is part of a set and cannot be purchased individually. Please choose a Discovery Set.';
  const READY_TIMEOUT_MS = 3000; // Max time to hold a /cart/add waiting for blocklist

  /**
   * A promise that resolves when the blocklist has finished loading
   * (either from sessionStorage cache or from the /products.json fetch).
   * Until then, any /cart/add request is held — see waitUntilReady().
   * The timeout in waitUntilReady() is the failsafe so legit traffic is
   * never stuck indefinitely if the catalog fetch fails for any reason.
   */
  let _readyResolve;
  const _readyPromise = new Promise(function (resolve) { _readyResolve = resolve; });

  function markReady() {
    window.LOCK_CART.ready = true;
    if (_readyResolve) {
      _readyResolve();
      _readyResolve = null;
    }
  }

  function waitUntilReady() {
    if (window.LOCK_CART.ready) return Promise.resolve();
    return Promise.race([
      _readyPromise,
      new Promise(function (resolve) {
        setTimeout(function () {
          if (!window.LOCK_CART.ready) {
            console.warn('[LOCK-CART] Timed out waiting for blocklist (' + READY_TIMEOUT_MS + 'ms). Allowing /cart/add through to avoid blocking legit traffic.');
          }
          resolve();
        }, READY_TIMEOUT_MS);
      })
    ]);
  }

  /**
   * Cache the blocked variant list in sessionStorage
   * Avoids re-fetching the entire catalog on every page navigation.
   * Cache expires after 1 hour, or can be cleared by reloading with ?lock-cart-refresh
   */
  const CACHE_KEY = 'lock_cart_blocked_variants';
  const CACHE_EXPIRY_KEY = 'lock_cart_cache_expiry';
  const CACHE_DURATION_MS = 60 * 60 * 1000; // 1 hour

  function loadFromCache() {
    try {
      const expiry = sessionStorage.getItem(CACHE_EXPIRY_KEY);
      if (!expiry || Date.now() > parseInt(expiry, 10)) return null;
      const cached = sessionStorage.getItem(CACHE_KEY);
      if (!cached) return null;
      return JSON.parse(cached);
    } catch (e) {
      return null;
    }
  }

  function saveToCache(variants) {
    try {
      sessionStorage.setItem(CACHE_KEY, JSON.stringify(variants));
      sessionStorage.setItem(CACHE_EXPIRY_KEY, String(Date.now() + CACHE_DURATION_MS));
    } catch (e) {
      // sessionStorage may be disabled or full — fail silently
    }
  }

  /**
   * Fetch a single page of products from Shopify
   */
  function fetchProductsPage(page) {
    return fetch('/products.json?limit=' + PAGE_SIZE + '&page=' + page)
      .then(function (res) { return res.json(); })
      .then(function (data) {
        return (data && data.products) ? data.products : [];
      })
      .catch(function () { return []; });
  }

  /**
   * Loop through ALL product pages and collect variant IDs of tagged products
   */
  function loadAllBlockedVariants() {
    // Allow forced refresh via URL param
    const forceRefresh = window.location.search.indexOf('lock-cart-refresh') !== -1;

    if (!forceRefresh) {
      const cached = loadFromCache();
      if (cached && cached.length >= 0) {
        window.LOCK_CART.blockedVariants = cached;
        markReady();
        console.log('[LOCK-CART] Loaded ' + cached.length + ' blocked variant(s) from cache');
        return;
      }
    }

    const blocked = [];
    let page = 1;

    function fetchNext() {
      if (page > MAX_PAGES) {
        console.warn('[LOCK-CART] Hit max page safety limit at ' + MAX_PAGES);
        finalize();
        return;
      }

      fetchProductsPage(page).then(function (products) {
        if (!products || products.length === 0) {
          // No more products — done
          finalize();
          return;
        }

        products.forEach(function (product) {
          if (product.tags && product.tags.indexOf(LOCK_TAG) !== -1) {
            if (product.variants && product.variants.length) {
              product.variants.forEach(function (variant) {
                blocked.push(String(variant.id));
              });
            }
          }
        });

        // If we got fewer than PAGE_SIZE products, this was the last page
        if (products.length < PAGE_SIZE) {
          finalize();
          return;
        }

        page++;
        fetchNext();
      });
    }

    function finalize() {
      window.LOCK_CART.blockedVariants = blocked;
      markReady();
      saveToCache(blocked);
      console.log('[LOCK-CART] Loaded ' + blocked.length + ' blocked variant(s) across ' + (page) + ' page(s)');
    }

    fetchNext();
  }

  /**
   * Check if a variant ID is in the blocked list
   */
  function isBlocked(variantId) {
    if (!variantId) return false;
    return window.LOCK_CART.blockedVariants.indexOf(String(variantId)) !== -1;
  }

  // Expose for sibling scripts (bundle-integrity.js).
  window.LOCK_CART.isBlocked = isBlocked;
  window.LOCK_CART.waitUntilReady = waitUntilReady;

  /**
   * Show a friendly block message to the customer
   */
  function showBlockMessage() {
    alert(BLOCK_MESSAGE);
  }

  /**
   * Parse a cart-add request body and extract every variant ID being added
   * plus the variant ID of any item declaring itself a bundle parent
   * (i.e. carrying the properties._bundle_product line item property).
   *
   * Supports all three formats Shopify accepts on /cart/add(.js):
   *   - single item:  id=12345&quantity=1
   *   - multi item URL-encoded:  items[0][id]=...&items[0][quantity]=1&...
   *   - multi item JSON:  {"items":[{"id":...,"properties":{...}}, ...]}
   * (FormData equivalents of the URL-encoded form also work.)
   *
   * Returns: { variantIds: [String], bundleParentVariantId: String|null }
   */
  function parseCartAddBody(body) {
    const result = { variantIds: [], bundleParentVariantId: null };
    if (!body) return result;

    const ITEM_KEY_RE = /^items\[(\d+)\]\[(\w+)\](?:\[(.+)\])?$/;

    function collectItems(items) {
      Object.keys(items).forEach(function (k) {
        const item = items[k];
        if (item.id) result.variantIds.push(item.id);
        if (item.isBundleParent && item.id) {
          result.bundleParentVariantId = item.id;
        }
      });
    }

    function applyEntry(items, key, value) {
      if (key === 'id' && value) {
        result.variantIds.push(String(value));
        return;
      }
      const m = key.match(ITEM_KEY_RE);
      if (!m) return;
      const idx = m[1];
      const field = m[2];
      const subfield = m[3];
      if (!items[idx]) items[idx] = {};
      if (field === 'id' && value) {
        items[idx].id = String(value);
      } else if (field === 'properties' && subfield === '_bundle_product') {
        items[idx].isBundleParent = true;
      }
    }

    function walkEntries(iter) {
      const items = {};
      let entry = iter.next();
      while (!entry.done) {
        applyEntry(items, entry.value[0], entry.value[1]);
        entry = iter.next();
      }
      collectItems(items);
    }

    if (body instanceof FormData) {
      walkEntries(body.entries());
      return result;
    }

    if (typeof body !== 'string') return result;

    // JSON body (used by some fetch callers)
    const trimmed = body.trim();
    if (trimmed.charAt(0) === '{' || trimmed.charAt(0) === '[') {
      try {
        const json = JSON.parse(trimmed);
        if (json && json.id) result.variantIds.push(String(json.id));
        if (json && Array.isArray(json.items)) {
          json.items.forEach(function (item) {
            if (!item) return;
            if (item.id) result.variantIds.push(String(item.id));
            if (item.properties && item.properties._bundle_product && item.id) {
              result.bundleParentVariantId = String(item.id);
            }
          });
        }
        return result;
      } catch (e) { /* fall through to URL-encoded */ }
    }

    // URL-encoded body (used by jQuery $.ajax default, and by the bundle JS)
    try {
      walkEntries(new URLSearchParams(body).entries());
    } catch (e) { /* ignore */ }

    return result;
  }

  /**
   * Given a parsed cart-add request, decide whether it should be blocked.
   * Returns the offending blocked variant ID, or null if the request is OK.
   *
   * Rule: if any item being added is on the lock-cart blocklist, the request
   * is only allowed when it also declares a legitimate bundle parent — an
   * item carrying properties._bundle_product whose own variant is NOT itself
   * locked. This lets the Discovery Set's 5-children-plus-parent payload
   * through while catching orphan-child exploits AND the "fake parent on a
   * locked $0 variant" trick.
   */
  function shouldBlock(parsed) {
    const offender = parsed.variantIds.find(isBlocked);
    if (!offender) return null;
    if (parsed.bundleParentVariantId && !isBlocked(parsed.bundleParentVariantId)) {
      return null;
    }
    return offender;
  }

  /**
   * Intercept fetch() calls to /cart/add. If the blocklist isn't ready
   * yet, hold the request via waitUntilReady() so an app firing on first
   * page load can't slip a locked variant through during the brief
   * window before /products.json finishes paginating.
   */
  const originalFetch = window.fetch;
  window.fetch = function () {
    const args = arguments;
    const self = this;
    const url = args[0];
    const urlString = (typeof url === 'string') ? url : (url && url.url) || '';

    if (urlString.indexOf('/cart/add') === -1) {
      return originalFetch.apply(self, args);
    }

    return waitUntilReady().then(function () {
      const opts = args[1] || {};
      const offender = shouldBlock(parseCartAddBody(opts.body));
      if (offender) {
        console.warn('[LOCK-CART] Blocked fetch add-to-cart for variant ' + offender);
        showBlockMessage();
        return Promise.reject(new Error('LOCK_CART: variant ' + offender + ' is not available individually'));
      }
      return originalFetch.apply(self, args);
    });
  };

  /**
   * Intercept XMLHttpRequest open() calls to /cart/add
   */
  const originalXhrOpen = XMLHttpRequest.prototype.open;
  const originalXhrSend = XMLHttpRequest.prototype.send;

  XMLHttpRequest.prototype.open = function (method, url) {
    this._lockCartUrl = url;
    return originalXhrOpen.apply(this, arguments);
  };

  XMLHttpRequest.prototype.send = function (body) {
    if (!this._lockCartUrl || this._lockCartUrl.indexOf('/cart/add') === -1) {
      return originalXhrSend.apply(this, arguments);
    }

    // Defer the underlying send() until the blocklist is ready. XHR is
    // already async from the caller's perspective, so holding the
    // network call for up to READY_TIMEOUT_MS is safe.
    const xhr = this;
    const args = arguments;
    waitUntilReady().then(function () {
      const offender = shouldBlock(parseCartAddBody(body));
      if (offender) {
        console.warn('[LOCK-CART] Blocked XHR add-to-cart for variant ' + offender);
        showBlockMessage();
        return; // never call original send → request never fires
      }
      originalXhrSend.apply(xhr, args);
    });
  };

  /**
   * Catch traditional form submissions to /cart/add. When the blocklist
   * isn't ready yet, we preventDefault, wait, re-evaluate, and (if safe)
   * programmatically re-submit via form.submit() — which does NOT
   * re-trigger this listener per the HTML spec, so we won't loop.
   */
  function parseFormForCart(form) {
    const parsed = { variantIds: [], bundleParentVariantId: null };
    const items = {};

    Array.prototype.forEach.call(form.elements, function (el) {
      if (!el.name) return;
      if (el.name === 'id' && el.value) {
        parsed.variantIds.push(String(el.value));
        return;
      }
      const m = el.name.match(/^items\[(\d+)\]\[(\w+)\](?:\[(.+)\])?$/);
      if (!m) return;
      const idx = m[1], field = m[2], subfield = m[3];
      if (!items[idx]) items[idx] = {};
      if (field === 'id' && el.value) items[idx].id = String(el.value);
      else if (field === 'properties' && subfield === '_bundle_product') {
        items[idx].isBundleParent = true;
      }
    });
    Object.keys(items).forEach(function (k) {
      const item = items[k];
      if (item.id) parsed.variantIds.push(item.id);
      if (item.isBundleParent && item.id) parsed.bundleParentVariantId = item.id;
    });
    return parsed;
  }

  document.addEventListener('submit', function (e) {
    const form = e.target;
    if (!form || !form.action) return;
    if (form.action.indexOf('/cart/add') === -1) return;

    // Not ready yet → defer the submit until we are
    if (!window.LOCK_CART.ready) {
      e.preventDefault();
      e.stopPropagation();
      waitUntilReady().then(function () {
        const offender = shouldBlock(parseFormForCart(form));
        if (offender) {
          console.warn('[LOCK-CART] Blocked form submission for variant ' + offender);
          showBlockMessage();
          return;
        }
        form.submit(); // does not re-fire this submit listener
      });
      return;
    }

    const offender = shouldBlock(parseFormForCart(form));
    if (!offender) return;

    e.preventDefault();
    e.stopPropagation();
    console.warn('[LOCK-CART] Blocked form submission for variant ' + offender);
    showBlockMessage();
  }, true);

  /**
   * Catch direct URL navigation to /cart/add?id=... (when typed in browser)
   */
  if (window.location.pathname === '/cart/add' || window.location.pathname.indexOf('/cart/add') === 0) {
    const params = new URLSearchParams(window.location.search);
    const variantId = params.get('id');
    if (variantId) {
      const checkAndRedirect = function () {
        if (!window.LOCK_CART.ready) {
          setTimeout(checkAndRedirect, 100);
          return;
        }
        if (isBlocked(variantId)) {
          console.warn('[LOCK-CART] Blocked direct URL access to variant ' + variantId);
          alert(BLOCK_MESSAGE);
          window.location.href = '/collections/discovery-sets';
        }
      };
      checkAndRedirect();
    }
  }

  // Initialize
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', loadAllBlockedVariants);
  } else {
    loadAllBlockedVariants();
  }

})();